import {skipToken} from '@reduxjs/toolkit/query';
import {closeCurrentDialog, openDialog} from 'platform/components';
import {Box, Grid, HStack, Show, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query, CustomerResponseBodyV2} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useChangeWorkshopInterestContactPersonMutation,
  useGetWorkshopInterestQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {CustomerMatchOrCreate, CustomerWidgetCard, handleApiError, Section} from '@dms/shared';

type ContactPersonProps = {
  interestId: string;
};

export function ContactPerson(props: ContactPersonProps) {
  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: props.interestId});

  const {data: customer} = useGetCustomerV2Query(
    isNotNil(workshopInterest?.contactPersonId)
      ? {customerId: workshopInterest.contactPersonId}
      : skipToken
  );

  const [changeContactPerson] = useChangeWorkshopInterestContactPersonMutation();

  const onCustomer = (selectedCustomer: CustomerResponseBodyV2) =>
    changeContactPerson({
      id: props.interestId,
      changeWorkshopInterestContactPersonRequestBody: {
        contactPersonId: selectedCustomer.id,
      },
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);

  const handleCustomerChange = () =>
    openDialog(
      <CustomerMatchOrCreate
        data-testid={testIds.workshop.interestDetail('contactPerson-match')}
        onCustomer={onCustomer}
      />,
      {title: i18n.t('entity.customer.labels.customer')}
    );

  return (
    <Section>
      <Show when={isNotNil(customer)}>
        <Grid columns={2}>
          <VStack spacing={4}>
            <CustomerWidgetCard
              resourceId={EntityResourceIds.workshopInterest}
              recordId={props.interestId}
              isContactPersonHidden
              customerId={customer?.id}
              onCustomer={onCustomer}
              onChange={handleCustomerChange}
              secondStepComponentType="BUSINESS_CASE"
              data-testid={testIds.workshop.interestDetail('contactPerson')}
              isDetailLinkVisible
              canChangeCustomer
            />
          </VStack>
        </Grid>
      </Show>

      <Show when={!isNotNil(customer)}>
        <HStack justify="center">
          <Box width={188}>
            <CustomerWidgetCard
              onCustomer={onCustomer}
              secondStepComponentType="BUSINESS_CASE"
              data-testid={testIds.workshop.interestDetail('contactPerson')}
              canChangeCustomer
            />
          </Box>
        </HStack>
      </Show>
    </Section>
  );
}
